import { lazy as reactLazy, type ComponentType, type LazyExoticComponent } from 'react'

type Loader = () => Promise<{ default: ComponentType }>
const loaders = new WeakMap<object, Loader>()
/** React.lazy that remembers its loader so routes can be preloaded before the user clicks. */
function lazy(factory: Loader) {
  const component = reactLazy(factory)
  loaders.set(component, factory)
  return component
}

/**
 * Implemented screens, keyed by route path. Each is a separate chunk (code splitting per page).
 * Paths in the navigation tree without an entry here render ScheduledPage / IntegrationGapPage.
 */
const txn = (name: 'ChangeStatusPages' | 'LostPages' | 'CellsPages' | 'DrsPages' | 'TransferPages' | 'VisitPages' | 'RtsPages' | 'PayToCustPages' | 'AgentPaymentPages' | 'InvoicePages' | 'BranchPaymentPages', view: 'List' | 'New' | 'Detail') =>
  lazy(() => import('@/features/transactions/TransactionPages').then((m) => ({ default: m[name][view] })))

const pickup = (view: 'Inquiry' | 'Assign' | 'Receive' | 'New') => lazy(() => import('@/features/pickups/PickupsPages').then((m) => ({ default: m.PickupPages[view] })))

const fieldOps = (view: 'RunnerReturns' | 'Receive' | 'Scan' | 'PodReview') => lazy(() => import('@/features/operations/FieldOpsPages').then((m) => ({ default: m.FieldOpsPages[view] })))

import { RESOURCES } from '@/features/resources/configs'
import { PAGE_HUBS } from '@/navigation/pageHubs'

const resource = (path: string) => lazy(() => import('@/features/resources/ResourceRoute').then((m) => ({ default: m.resourceScreen(path) })))

const admin = (view: 'Roles' | 'Permissions' | 'Audit' | 'Activity' | 'SystemLogs' | 'Security') => lazy(() => import('@/features/admin/AdminPages').then((m) => ({ default: m.AdminPages[view] })))

const shipmentAction = (view: 'Create' | 'Edit' | 'ManageAwbs' | 'Reoperate' | 'EditWeight' | 'Commission' | 'ManageTransactions' | 'UploadExcel') => lazy(() => import('@/features/shipments/ShipmentActionsPages').then((m) => ({ default: m.ShipmentActionPages[view] })))


const entity = (key: string, status?: string, title?: { en: string; ar: string }) => lazy(() => import('@/features/native/NativeRoutes').then((m) => ({ default: m.entityScreen(key, status, title) })))
const native = (view: 'Sla' | 'Warehouse' | 'Approvals' | 'Alerts' | 'AlertsOperations' | 'AlertsFinance' | 'AlertsCs' | 'AlertsSystem' | 'AlertsIntegrations' | 'NotificationSettings' | 'IntegrationLogs') => lazy(() => import('@/features/native/NativePages').then((m) => ({ default: m.NativePages[view] })))

/** Aliens OS native modules (gateway backend). Declared after RESOURCES so they replace failing Dispatch screens. */
const NATIVE_ROUTES = {
  '/native/:entity': lazy(() => import('@/features/native/NativeRoutes')),
  '/warehouse/bagging': entity('bags'),
  '/warehouse': native('Warehouse'),
  '/warehouse/reports': native('Warehouse'),
  '/linehaul/trips': entity('trips'),
  '/linehaul/trips/new': entity('trips', 'planned'),
  '/linehaul/trips/assign-vehicle': entity('trips', 'planned'),
  '/linehaul/trips/assign-driver': entity('trips', 'planned'),
  '/linehaul/trips/send': entity('trips', 'loading'),
  '/linehaul/trips/receive': entity('trips', 'departed'),
  '/linehaul/trips/tracking': entity('trips', 'departed'),
  '/runners/attendance': entity('attendance'),
  '/admin/attendance': entity('attendance'),
  '/runners/documents': entity('runnerDocuments'),
  '/runners/coverage': entity('runnerCoverage'),
  '/admin/departments': entity('departments'),
  '/admin/seasons': entity('seasons'),
  '/customer-service/escalations': entity('escalations'),
  '/customer-service/calls': entity('calls'),
  '/customer-service/follow-ups': entity('calls', 'followUp'),
  '/customer-service/call-history': entity('calls', 'done'),
  '/runners/payments': entity('runnerPayouts'),
  '/finance/branch-payment-requests': entity('branchPaymentRequests'),
  '/cod/requests': entity('collectionRequests'),
  '/integrations/webhooks': entity('webhooks'),
  '/aliens-courier/whatsapp/rules': entity('whatsappReasonRules'),
  '/aliens-courier/update-checks': entity('whatsappUpdateChecks'),
  '/aliens-courier/ratings': entity('whatsappRatings'),
  '/integrations/payment-gateways': lazy(() => import('@/features/integrations/PaymentGatewaysPage')),
  '/integrations/email': lazy(() => import('@/features/integrations/EmailSettingsPage')),
  '/integrations/logs': native('IntegrationLogs'),
  '/reports/delivery-performance': native('Sla'),
  '/reports/audit': admin('Audit'),
  '/notifications/operations': native('AlertsOperations'),
  '/notifications/finance': native('AlertsFinance'),
  '/notifications/customer-service': native('AlertsCs'),
  '/notifications/integrations': native('AlertsIntegrations'),
  '/customer-service/enjad-chat': lazy(() => import('@/features/customerService/EnjadInboxPage')),
  '/customer-service/courier-chats': lazy(() => import('@/features/customerService/CourierChatsPage')),
  '/customer-service/flowly-tasks': lazy(() => import('@/features/customerService/CsTasksPage')),
  '/customer-service/pickup-reminder': lazy(() => import('@/features/customerService/PickupReminderPage')),
  '/integrations/enjad/return-sheets': lazy(() => import('@/features/integrations/EnjadReturnSheetsPage')),
  '/notifications/approvals': native('Approvals'),
  '/notifications/settings': native('NotificationSettings'),
  '/notifications/alerts': native('Alerts'),
}


const analysis = (view: import('@/features/analysis/AnalysisPages').AnalysisView) => lazy(() => import('@/features/analysis/AnalysisPages').then((m) => ({ default: m.AnalysisPages[view] })))

/** Analysis section ("تحليلات") — computed by the gateway from Dispatch shipments. */
const ANALYSIS_ROUTES = {
  '/analysis': analysis('Overview'),
  '/analysis/charts': analysis('Charts'),
  '/analysis/operations': analysis('Operations'),
  '/analysis/shipments': analysis('Shipments'),
  // pickup tracking lives with the other pickup screens: cities, accounts and sub accounts, not just clients
  '/analysis/pickups': lazy(() => import('@/features/pickups/PickupTrackingPage').then((m) => ({ default: m.PickupTrackingPage }))),
  '/analysis/returns': analysis('Returns'),
  '/analysis/financial': analysis('Financial'),
  '/analysis/aging': analysis('Aging'),
  '/analysis/lifecycle': analysis('Lifecycle'),
  '/analysis/violations': analysis('Violations'),
  '/analysis/intelligence': analysis('Intelligence'),
  '/analysis/whatsapp': analysis('WhatsApp'),
  '/analysis/clients': analysis('Clients'),
  '/analysis/couriers': analysis('Couriers'),
  '/analysis/branches': analysis('Branches'),
  '/analysis/cities': analysis('Cities'),
  '/analysis/zones': analysis('Zones'),
  '/analysis/reasons': analysis('Reasons'),
  '/analysis/recovery': analysis('Recovery'),
  '/analysis/statuses': analysis('Statuses'),
  '/analysis/customers': analysis('Customers'),
  '/analysis/corporate-customers': analysis('CorporateCustomers'),
  '/analysis/employees': analysis('Employees'),
  '/analysis/seasons': analysis('Seasons'),
  '/analysis/compare': analysis('Compare'),
  '/analysis/report': analysis('Report'),
}

const VSOFT_FILES = {
  ReportPages: () => import('@/features/vsoft/ReportPages').then((m) => m.ReportPages as Record<string, ComponentType>),
  CallCenterPages: () => import('@/features/vsoft/CallCenterPages').then((m) => m.CallCenterPages as Record<string, ComponentType>),
  AdminSettingsPages: () => import('@/features/vsoft/AdminSettingsPages').then((m) => m.AdminSettingsPages as Record<string, ComponentType>),
}
const vsoft = (file: keyof typeof VSOFT_FILES, view: string) => lazy(() => VSOFT_FILES[file]().then((pages) => ({ default: pages[view]! })))

/**
 * Legacy VSoft screens (Reports / Customer Service / Branches / Line Haul / Admin / Settings / Fulfillment) that need more
 * than a config-driven list. Declared last so they replace the generic entries of the same path (e.g. /reports/audit).
 */
const VSOFT_ROUTES = {
  '/reports/runner-activity': vsoft('ReportPages', 'RunnerActivity'),
  '/reports/track-routes': vsoft('ReportPages', 'TrackRoutes'),
  // the runners map (ported from Aliens Courier): GPS roads, coverage, parcels, one runner's day, the office's live map
  '/reports/track-runners': lazy(() => import('@/features/runnersMap/RunnersMapPage')),
  // a runner's own status update vs what VSoft actually confirmed (ported from Aliens Courier, 2026-09-25)
  '/settings/vsoft': lazy(() => import('@/features/settings/VsoftSettingsPage')),
  '/whatsapp/console': lazy(() => import('@/features/whatsapp/WaConsolePage')),
  '/runners/app-user': lazy(() => import('@/features/runners/RunnerAppUserPage')),
  '/runners/new': lazy(() => import('@/features/runners/RunnerCreatePage')),
  '/customer-service/urgent': lazy(() => import('@/features/customerService/UrgentShipmentsPage')),
  '/runners/update-track': lazy(() => import('@/features/runners/RunnerUpdateTrackPage')),
  '/reports/audit': resource('/reports/audit'),
  '/customer-service/call-center-plan': vsoft('CallCenterPages', 'CallPlan'),
  '/customer-service/current-calls': vsoft('CallCenterPages', 'CurrentCalls'),
  '/customer-service/notes': vsoft('CallCenterPages', 'ShipmentNotes'),
  '/admin/users-branches-auth': vsoft('AdminSettingsPages', 'UsersBranches'),
  '/admin/integration-data': vsoft('AdminSettingsPages', 'IntegrationData'),
  '/settings/zones': vsoft('AdminSettingsPages', 'Zones'),
  '/settings/general': vsoft('AdminSettingsPages', 'CompanyProfile'),
}

const ledger = (view: 'Journal' | 'Accounts' | 'Reports' | 'Periods') => lazy(() => import('@/features/controlRoom/LedgerPages').then((m) => ({ default: m.LedgerPages[view] })))
const adminExtra = (view: 'Activity' | 'MasterData' | 'Profile') => lazy(() => import('@/features/controlRoom/AdminExtraPages').then((m) => ({ default: m.AdminExtraPages[view] })))

/** Old Aliens Dashboard parity (gateway src/ops): control room, Aliens Courier telemetry, general ledger, admin extras. */
const CONTROL_ROUTES = {
  '/ops/command-center': lazy(() => import('@/features/controlRoom/CommandCenterPage')),
  '/ops/emergency': lazy(() => import('@/features/controlRoom/EmergencyPage')),
  '/ops/alert-settings': lazy(() => import('@/features/controlRoom/AlertRulesPage')),
  '/aliens-courier/confirmations': lazy(() => import('@/features/controlRoom/WhatsAppConfirmationsPage')),
  '/aliens-courier/courier-app': lazy(() => import('@/features/controlRoom/CourierAppPage')),
  '/accounting': ledger('Journal'),
  '/accounting/accounts': ledger('Accounts'),
  '/accounting/ledger': ledger('Reports'),
  '/accounting/periods': ledger('Periods'),
  '/accounting/cash': lazy(() => import('@/features/accounting/CashDeskPage')),
  '/fulfillment': lazy(() => import('@/features/warehouse/WarehousePages').then((m) => ({ default: m.WarehousePages.Overview }))),
  '/fulfillment/items': lazy(() => import('@/features/warehouse/WarehousePages').then((m) => ({ default: m.WarehousePages.Items }))),
  '/fulfillment/receive': lazy(() => import('@/features/warehouse/WarehousePages').then((m) => ({ default: m.WarehousePages.Receive }))),
  '/transport': lazy(() => import('@/features/transport/TransportPages').then((m) => ({ default: m.TransportPages.Overview }))),
  '/transport/bags': lazy(() => import('@/features/transport/TransportPages').then((m) => ({ default: m.TransportPages.Bags }))),
  '/transport/trips': lazy(() => import('@/features/transport/TransportPages').then((m) => ({ default: m.TransportPages.Trips }))),
  '/transport/receive': lazy(() => import('@/features/transport/TransportPages').then((m) => ({ default: m.TransportPages.Receive }))),
  '/transport/cases': lazy(() => import('@/features/transport/TransportPages').then((m) => ({ default: m.TransportPages.Cases }))),
  '/transport/setup': lazy(() => import('@/features/transport/TransportPages').then((m) => ({ default: m.TransportPages.Setup }))),
  '/fulfillment/inbounds': lazy(() => import('@/features/warehouse/WarehousePages').then((m) => ({ default: m.WarehousePages.Inbounds }))),
  '/fulfillment/orders': lazy(() => import('@/features/warehouse/WarehousePages').then((m) => ({ default: m.WarehousePages.Orders }))),
  '/fulfillment/fees': lazy(() => import('@/features/warehouse/WarehousePages').then((m) => ({ default: m.WarehousePages.Fees }))),
  '/fulfillment/settings': lazy(() => import('@/features/warehouse/WarehousePages').then((m) => ({ default: m.WarehousePages.Settings }))),
  '/fulfillment/stock': lazy(() => import('@/features/warehouse/WarehousePages').then((m) => ({ default: m.WarehousePages.Stock }))),
  '/fulfillment/locations': lazy(() => import('@/features/warehouse/WarehousePages').then((m) => ({ default: m.WarehousePages.Locations }))),
  '/fulfillment/moves': lazy(() => import('@/features/warehouse/WarehousePages').then((m) => ({ default: m.WarehousePages.Moves }))),
  '/cod/commission-edit': lazy(() => import('@/features/transactions/CommissionEditPage')),
  '/finance/compensations': lazy(() => import('@/features/finance/CompensationsPage')),
  '/accounting/profit': lazy(() => import('@/features/accounting/BooksPages').then((m) => ({ default: m.BooksPages.Profit }))),
  '/accounting/assets': lazy(() => import('@/features/accounting/BooksPages').then((m) => ({ default: m.BooksPages.Assets }))),
  '/hr/employees': lazy(() => import('@/features/hr/HrEmployeesPage')),
  '/hr/branches': lazy(() => import('@/features/hr/HrBranchesPage')),
  '/hr/audit': lazy(() => import('@/features/hr/HrAuditPage')),
  '/hr/notify': lazy(() => import('@/features/hr/HrNotifyPage')),
  '/hr/me': lazy(() => import('@/features/hr/MyAttendancePage')),
  '/hr/attendance': lazy(() => import('@/features/hr/HrAttendancePage')),
  '/hr/shifts': lazy(() => import('@/features/hr/HrShiftsPage')),
  '/hr/leaves': lazy(() => import('@/features/hr/HrLeavesPage')),
  '/hr/payroll': lazy(() => import('@/features/hr/HrPayrollPage')),
  '/accounting/payroll': lazy(() => import('@/features/accounting/BooksPages').then((m) => ({ default: m.BooksPages.Payroll }))),
  '/accounting/opening': lazy(() => import('@/features/accounting/BooksPages').then((m) => ({ default: m.BooksPages.Opening }))),
  '/admin/activity': adminExtra('Activity'),
  '/admin/master-data': adminExtra('MasterData'),
  '/profile': adminExtra('Profile'),
}

const SCREENS: Record<string, LazyExoticComponent<ComponentType>> = {
  '/': lazy(() => import('@/features/home/DashboardPage')),
  '/my': lazy(() => import('@/features/hr/MyHomePage')),
  '/branches/returns-transfer': lazy(() => import('@/features/branches/ReturnsTransferPage')),
  '/finance/return-pricing': lazy(() => import('@/features/finance/ReturnPricingPage')),
  '/operations/not-received': lazy(() => import('@/features/operations/NotReceivedPage')),
  '/operations/deleted-shipments': lazy(() => import('@/features/operations/DeletedShipmentsPage')),
  '/settings/camera-link': lazy(() => import('@/features/settings/CameraLinkPage')),
  '/settings/cameras-live': lazy(() => import('@/features/settings/CamerasLivePage')),
  '/settings/cameras-report': lazy(() => import('@/features/settings/CamerasReportPage')),
  '/operations/weights': lazy(() => import('@/features/operations/WeightsPage')),
  '/notifications': lazy(() => import('@/features/notifications/NotificationsPage')),
  '/support/error-reports': lazy(() => import('@/features/support/ErrorReportsPage').then((m) => ({ default: m.ErrorReportsPage }))),
  '/reports/awb-tracking': lazy(() => import('@/features/shipments/ShipmentTrackingPage')),
  '/reports/shipment-details': lazy(() => import('@/features/shipments/ShipmentDetailsPage')),
  '/cod/unpaid-shipping': lazy(() => import('@/features/shipments/UnpaidShippingPage')),
  '/finance/deposit-slips': lazy(() => import('@/features/finance/DepositSlipsPage')),
  '/cod/unlinked-payments': lazy(() => import('@/features/finance/UnlinkedPaymentsPage')),
  '/cod/branch-shipping-pay': lazy(() => import('@/features/shipments/BranchShippingPayPage')),
  '/finance/pending-payouts': lazy(() => import('@/features/finance/PendingPayoutsPage')),
  '/customer-service/client-updates': lazy(() => import('@/features/customerService/ClientUpdatesPage')),
  '/finance/payouts': lazy(() => import('@/features/finance/PayoutsPage')),
  '/finance/proofs': lazy(() => import('@/features/finance/PayoutProofsPage')),
  '/finance/deposits': lazy(() => import('@/features/deposits/DepositsPage')),
  '/finance/deposits/withdrawals': lazy(() => import('@/features/deposits/DepositWithdrawalsPage')),
  '/operations/shipments/bulk-clone': lazy(() => import('@/features/shipments/BulkClonePage')),
  '/finance/fawry': lazy(() => import('@/features/fawry/FawryPage')),
  '/finance/fawry-review': lazy(() => import('@/features/fawry/FawryReviewPage')),
  '/finance/payout-radar': lazy(() => import('@/features/finance/PayoutRadarPage')),
  '/finance/portal-payout-requests': lazy(() => import('@/features/finance/PortalPayoutRequestsPage')),
  '/notifications/portal-announcements': lazy(() => import('@/features/portalAnnouncements/PortalAnnouncementsPage')),
  '/notifications/portal-notices': lazy(() => import('@/features/portalNotices/PortalNoticesPage')),
  '/operations/returns-handover': lazy(() => import('@/features/returnsHandover/ReturnsHandoverPage')),
  '/clients/signup-attempts': lazy(() => import('@/features/signupAttempts/SignupAttemptsPage')),
  '/pickups/requests-track': lazy(() => import('@/features/pickups/PickupRequestsTrackPage')),
  '/materials': lazy(() => import('@/features/materials/MaterialsOrdersPage')),
  '/materials/new': lazy(() => import('@/features/materials/MaterialsNewOrderPage')),
  '/materials/products': lazy(() => import('@/features/materials/MaterialsProductsPage')),
  '/materials/settings': lazy(() => import('@/features/materials/MaterialsSettingsPage')),
  '/materials/:id': lazy(() => import('@/features/materials/MaterialsNewOrderPage')),
  '/operations/change-status': txn('ChangeStatusPages', 'List'),
  '/operations/change-status/new': txn('ChangeStatusPages', 'New'),
  '/operations/change-status/:id': txn('ChangeStatusPages', 'Detail'),
  '/operations/lost': txn('LostPages', 'List'),
  '/operations/lost/new': txn('LostPages', 'New'),
  '/operations/lost/:id': txn('LostPages', 'Detail'),
  '/operations/cells': txn('CellsPages', 'List'),
  '/operations/cells/new': txn('CellsPages', 'New'),
  '/operations/cells/:id': txn('CellsPages', 'Detail'),
  '/operations/drs': txn('DrsPages', 'List'),
  '/operations/drs/new': txn('DrsPages', 'New'),
  '/operations/drs/:id': txn('DrsPages', 'Detail'),
  '/branches/transfers': txn('TransferPages', 'List'),
  '/branches/transfers/new': txn('TransferPages', 'New'),
  '/branches/transfers/:id': txn('TransferPages', 'Detail'),
  '/pickups': pickup('Inquiry'),
  '/pickups/assign': pickup('Assign'),
  '/pickups/receive': pickup('Receive'),
  '/pickups/new': pickup('New'),
  '/pickups/portal-settings': lazy(() => import('@/features/pickups/PortalPickupSettingsPage')),
  '/operations/runner-returns': fieldOps('RunnerReturns'),
  '/operations/runner-returns/sounds': lazy(() => import('@/features/operations/VerdictSoundsPage')),
  '/whatsapp': lazy(() => import('@/features/whatsapp/WhatsAppHubPage')),
  '/whatsapp/templates': lazy(() => import('@/features/whatsapp/TemplatesPage')),
  '/admin/vsoft-traffic': lazy(() => import('@/features/admin/TrafficPage')),
  '/operations/shipments/upload-monitor': lazy(() => import('@/features/shipments/UploadMonitorPage')),
  '/operations/shipments/group-sheets': lazy(() => import('@/features/shipments/GroupSheetsPage')),
  '/operations/runner-returns/settings': lazy(() => import('@/features/operations/RunnerReturnsSettingsPage')),
  '/operations/shipments/receive': fieldOps('Receive'),
  '/operations/shipments/receipt-audit': lazy(() => import('@/features/whatsapp/ReceiptAuditPage').then((m) => ({ default: m.ReceiptAuditPage }))),
  '/operations/visit-result': txn('VisitPages', 'List'),
  '/operations/visit-result/new': txn('VisitPages', 'New'),
  '/operations/visit-result/:id': txn('VisitPages', 'Detail'),
  '/operations/return-to-shipper': txn('RtsPages', 'List'),
  '/operations/return-to-shipper/new': txn('RtsPages', 'New'),
  '/operations/return-to-shipper/:id': txn('RtsPages', 'Detail'),
  '/integrations/ka3bo': lazy(() => import('@/features/integrations/Ka3boPage')),
  '/integrations/ka3bo/settings': lazy(() => import('@/features/integrations/Ka3boSettingsPage')),
  '/integrations/mega': lazy(() => import('@/features/integrations/MegaCompaniesPage')),
  '/integrations/mega/:company': lazy(() => import('@/features/integrations/Ka3boPage')),
  '/integrations/mega/:company/settings': lazy(() => import('@/features/integrations/Ka3boSettingsPage')),
  '/integrations/enjad': lazy(() => import('@/features/integrations/EnjadPage')),
  '/integrations/lineexpress': lazy(() => import('@/features/integrations/LineExpressPage')),
  '/integrations/lineexpress/settings': lazy(() => import('@/features/integrations/LineExpressSettingsPage')),
  '/integrations/lineexpress/mapping': lazy(() => import('@/features/integrations/LineExpressMappingPage')),
  '/integrations/consignee-messages': lazy(() => import('@/features/integrations/ConsigneeNotifyPage')),
  '/integrations/whatsapp-messages': lazy(() => import('@/features/integrations/WhatsAppMessagesPage')),
  '/integrations/attempts-alert': lazy(() => import('@/features/integrations/AttemptsAlertPage')),
  '/settings/notifications-hub': lazy(() => import('@/features/notifications/NotificationsHubPage')),
  '/settings/system': lazy(() => import('@/features/settings/SystemSettingsPage')),
  '/settings/portal-payouts': lazy(() => import('@/features/settings/PortalPayoutMethodsPage')),
  '/settings/client-groups': lazy(() => import('@/features/settings/ClientGroupsPage')),
  '/aliens-courier/whatsapp': lazy(() => import('@/features/integrations/WhatsAppCloudPage')),
  '/aliens-courier/shipment-timeline': lazy(() => import('@/features/integrations/ShipmentTimelinePage')),
  '/aliens-courier/whatsapp-failures': lazy(() => import('@/features/integrations/WhatsAppFailuresPage')),
  '/aliens-courier/group-broadcast': lazy(() => import('@/features/integrations/GroupBroadcastPage')),
  '/integrations/partner-webhooks': lazy(() => import('@/features/integrations/PartnerHooksPage')),
  '/guides': lazy(() => import('@/features/docs/GuidesPage')),
  '/integrations/webhook-requests': lazy(() => import('@/features/integrations/WebhookRequestsPage')),
  '/admin/ui-settings': lazy(() => import('@/features/admin/UiSettingsPage')),
  '/admin/user-access': lazy(() => import('@/features/admin/UserAccessPage')),
  '/admin/diagnostics': lazy(() => import('@/features/admin/DiagnosticsPage')),
  '/admin/performance': lazy(() => import('@/features/admin/PerformancePage')),
  '/admin/roles': admin('Roles'),
  '/admin/permissions': admin('Permissions'),
  '/admin/audit': admin('Audit'),
  '/admin/user-activity': admin('Activity'),
  '/admin/system-logs': admin('SystemLogs'),
  '/settings/security': admin('Security'),
  '/operations/shipments/new': shipmentAction('Create'),
  '/operations/shipments/edit': shipmentAction('Edit'),
  '/operations/shipments/upload': shipmentAction('UploadExcel'),
  // the move-account permission has no screen of its own: it lives in the uploaded sheet's details
  '/operations/shipments/upload/move-account': shipmentAction('UploadExcel'),
  '/operations/awbs': shipmentAction('ManageAwbs'),
  '/operations/reoperate': shipmentAction('Reoperate'),
  '/operations/closed-redistribute': lazy(() => import('@/features/shipments/ClosedRedistributePage').then((m) => ({ default: m.ClosedRedistributePage }))),
  '/operations/edit-weight': shipmentAction('EditWeight'),
  '/finance/commission': lazy(() => import('@/features/finance/CommissionPage')),
  '/operations/transactions': shipmentAction('ManageTransactions'),
  '/customer-service/customer-search': lazy(() => import('@/features/shipments/ShipmentSearchPage')),
  '/customer-service/shipment-chat': lazy(() => import('@/features/controlRoom/ShipmentChatPage').then((m) => ({ default: m.ShipmentChatPage }))),
  '/returns/receiving': fieldOps('RunnerReturns'),
  '/warehouse/scan': fieldOps('Scan'),
  '/home/quick-actions': lazy(() => import('@/features/home/QuickActionsPage')),
  '/customer-service/shipment-search': lazy(() => import('@/features/shipments/ShipmentSearchPage')),
  ...Object.fromEntries(Object.keys(RESOURCES).map((path) => [path, resource(path)])),
  // Live Operations = the runner map (the raw GetLiveLocation list showed bare coordinates / speed / accuracy)
  '/home/live': vsoft('ReportPages', 'TrackRunners'),
  // client portal users + API token (VSoft keeps them behind Manage Clients row actions) — replaces the read-only list
  '/clients/users': lazy(() => import('@/features/clients/ClientPortalUsersPage')),
  // finance transaction workflows — declared after RESOURCES so they replace the read-only generic lists
  '/finance/pay-to-customer': txn('PayToCustPages', 'List'),
  '/finance/pay-to-customer/new': txn('PayToCustPages', 'New'),
  '/finance/pay-to-customer/:id': txn('PayToCustPages', 'Detail'),
  '/cod/agent-collections': lazy(() => import('@/features/finance/AgentCollectionsPage').then((m) => ({ default: m.AgentCollectionsPage }))),
  // VSoft's Runners Collection Inquiry with today, the branch and its runners (owner, 2026-10-24)
  '/cod/runner-collections': lazy(() => import('@/features/finance/RunnerCollectionInquiryPage')),
  '/cod/runner-payments': txn('AgentPaymentPages', 'List'),
  '/cod/runner-payments/new': txn('AgentPaymentPages', 'New'),
  '/cod/runner-payments/daily': lazy(() => import('@/features/finance/RunnerCollectionDailyPage')),
  '/cod/runner-payments/:id': txn('AgentPaymentPages', 'Detail'),
  '/finance/invoices': txn('InvoicePages', 'List'),
  '/finance/invoices/new': txn('InvoicePages', 'New'),
  '/finance/invoices/:id': txn('InvoicePages', 'Detail'),
  '/finance/branch-payments': txn('BranchPaymentPages', 'List'),
  '/finance/branch-payments/new': txn('BranchPaymentPages', 'New'),
  '/finance/branch-payments/:id': txn('BranchPaymentPages', 'Detail'),
  // finance screens rebuilt after VSoft (Customer Statements, Pending Collections, Payment Review, Add Client stepper)
  '/finance/statements': lazy(() => import('@/features/finance/CustomerStatementsPage')),
  '/cod/pending': lazy(() => import('@/features/finance/PendingCollectionsPage')),
  '/cod/review': lazy(() => import('@/features/finance/PaymentReviewPage')),
  '/clients/new': lazy(() => import('@/features/finance/AddClientPage')),
  // VSoft "POD Review" — its own screen (Runner / From / To + the POD grid), replacing the generic resource list
  '/operations/pod-review': fieldOps('PodReview'),
  '/operations/labels': lazy(() => import('@/features/labels/LabelDesignerPage')),
  ...NATIVE_ROUTES,
  ...ANALYSIS_ROUTES,
  ...VSOFT_ROUTES,
  ...CONTROL_ROUTES,
}

/**
 * A hub's home shows its own screen as the first tab and the other pages of its group as the next ones
 * (navigation/pageHubs.ts, owner 2026-10-29): its address opens the hub, and its own screen is kept here.
 */
export const HUB_HOME_SCREENS: Record<string, LazyExoticComponent<ComponentType>> = Object.fromEntries(PAGE_HUBS.map((h) => [h.home, SCREENS[h.path]!]))
const hubPage = (home: string) => lazy(() => import('@/features/hubs/PageHubPage').then((m) => ({ default: m.hubScreen(home) })))

export const PAGE_REGISTRY: Record<string, LazyExoticComponent<ComponentType>> = {
  ...SCREENS,
  ...Object.fromEntries(PAGE_HUBS.map((h) => [h.path, hubPage(h.home)])),
}

const preloaded = new Set<string>()
/** Starts downloading a route's code (hover / focus / idle). Safe to call repeatedly. */
export function preloadPath(path: string) {
  const clean = path.split('?')[0]!
  if (preloaded.has(clean)) return
  const component = PAGE_REGISTRY[clean]
  const loader = component && loaders.get(component)
  if (!loader) return
  preloaded.add(clean)
  void loader().catch(() => preloaded.delete(clean))
}
